import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import styles from './LandingPageSection3.module.css';

import img1 from '../../../../Images/avatar image/01.png';
import img2 from '../../../../Images/avatar image/02.png';
import img3 from '../../../../Images/avatar image/03.png';
import img4 from '../../../../Images/avatar image/04.png';
import img5 from '../../../../Images/avatar image/05.png';
import img6 from '../../../../Images/avatar image/06.png';

import voice1 from '../../../../RecordedVoices/realEstate.wav';
import voice2 from '../../../../RecordedVoices/lead.wav';
import voice3 from '../../../../RecordedVoices/hospital.wav';
import voice4 from '../../../../RecordedVoices/assistent.wav';
import voice5 from '../../../../RecordedVoices/customer.wav';
import voice6 from '../../../../RecordedVoices/support.wav';

const aiAgents = [
  {
    agentId: 1,
    imageUrl: img1,
    name: 'Aria',
    role: 'Support Specialist',
    description: 'Answers FAQs & triages issues',
    voiceSample: voice1,
  },
  {
    agentId: 2,
    imageUrl: img2,
    name: 'Marcus',
    role: 'Sales Closer',
    description: 'Books demos and qualifies leads',
    voiceSample: voice2,
  },
  {
    agentId: 3,
    imageUrl: img3,
    name: 'Priya',
    role: 'Scheduler',
    description: 'Handles bookings and reminders',
    voiceSample: voice3,
  },
  {
    agentId: 4,
    imageUrl: img4,
    name: 'Leo',
    role: 'Recruiter',
    description: 'Screens candidates over the phone',
    voiceSample: voice4,
  },
  {
    agentId: 5,
    imageUrl: img5,
    name: 'Nova',
    role: 'Concierge',
    description: 'Personalized VIP customer care',
    voiceSample: voice5,
  },
  {
    agentId: 6,
    imageUrl: img6,
    name: 'Kai',
    role: 'Collections',
    description: 'Empathetic payment follow-ups',
    voiceSample: voice6,
  },
];

gsap.registerPlugin(ScrollTrigger);

const LandingPageSection3 = () => {
  const navigate = useNavigate();
  const [playingAgentId, setPlayingAgentId] = useState(null);
  const currentAudioRef = useRef(null);
  const gridRef = useRef(null);

  const toggleAudio = (agent) => {
    if (playingAgentId === agent.agentId) {
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current = null;
      }
      setPlayingAgentId(null);
    } else {
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
      }

      const newAudio = new Audio(agent.voiceSample);
      newAudio.play().catch(err => console.log("Audio play error: ", err));

      currentAudioRef.current = newAudio;
      setPlayingAgentId(agent.agentId);

      newAudio.onended = () => {
        setPlayingAgentId(null);
        currentAudioRef.current = null;
      };
    }
  };

  useEffect(() => {
    const cards = gridRef.current.querySelectorAll(`.${styles.card}`);
    
    gsap.fromTo(
      cards,
      { opacity: 0, y: 50 },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: "power2.out",
        stagger: 0.15,
        scrollTrigger: {
          trigger: gridRef.current,
          start: "top 80%"
        }
      }
    );

    return () => {
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
      }
    };
  }, []);

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <h2 className={styles.title}>
          Meet your AI team — <span className={styles.gradientText}>smart agents tailored to your business</span>.
        </h2>
        <p className={styles.subtitle}>
          Pick a specialist, plug in your knowledge base, launch in minutes. Tap any agent to hear their voice.
        </p>

        <div className={styles.grid} ref={gridRef}>
          {aiAgents.map((agent) => {
            const isPlaying = playingAgentId === agent.agentId;

            return (
              <div
                key={agent.agentId}
                className={`${styles.card} ${isPlaying ? styles.activeCard : ''}`}
                onClick={() => toggleAudio(agent)}
              >
                {/* Header row: profile, name/role, audio btn */}
                <div className={styles.cardHeader}>
                  <div className={styles.userInfo}>
                    <div className={styles.avatarContainer}>
                      <img src={agent.imageUrl} alt={agent.name} className={styles.avatar} />
                    </div>
                    <div className={styles.nameContainer}>
                      <h3 className={styles.name}>{agent.name}</h3>
                      <p className={styles.role}>{agent.role}</p>
                    </div>
                  </div>
                  <button className={`${styles.playBtn} ${isPlaying ? styles.isPlayingBtn : ''}`}>
                    <span className={styles.playIcon}>{isPlaying ? '⏸' : '🔊'}</span>
                  </button>
                </div>

                {/* Description */}
                <p className={styles.description}>{agent.description}</p>

                {/* Soundwave visualizer */}
                <div className={`${styles.visualizer} ${isPlaying ? styles.activeVisualizer : ''}`}>
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            );
          })}
        </div>

        <div className={styles.ctaWrapper}>
          <button className={styles.ctaBtn} onClick={() => navigate('/login')}>
            Let your AI team work for you 24/7 →
          </button>
        </div>
      </div>
    </section>
  );
};

export default LandingPageSection3;